import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, KeyRound, ShieldCheck, Shield, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  super_admin: { label: "Super admin", color: "border-[#D95D39] text-[#D95D39] bg-[#D95D39]/5", Icon: ShieldCheck },
  admin: { label: "Admin", color: "border-[#0A1128] text-[#0A1128] bg-[#0A1128]/5", Icon: Shield },
  user: { label: "User", color: "border-[#8A8F98] text-[#8A8F98]", Icon: UserIcon },
};

export default function UsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", role: "user" });
  const [resetting, setResetting] = useState(null); // user being reset
  const [newPassword, setNewPassword] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data || []));

  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      return toast.error("Nombre, email y contraseña son obligatorios");
    }
    if (form.password.length < 6) return toast.error("La contraseña debe tener mínimo 6 caracteres");
    try {
      await api.post("/users", form);
      toast.success(`Usuario ${form.name} creado`);
      setForm({ name: "", email: "", password: "", company: "", role: "user" });
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear usuario");
    }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Rol actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el rol");
    }
  };

  const removeUser = async (id, name) => {
    if (!window.confirm(`¿Borrar al usuario "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario borrado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo borrar");
    }
  };

  const resetPassword = async () => {
    if (!resetting) return;
    if (!newPassword || newPassword.length < 6) return toast.error("Mínimo 6 caracteres");
    try {
      await api.patch(`/users/${resetting.id}`, { password: newPassword });
      toast.success(`Contraseña de ${resetting.name} actualizada`);
      setResetting(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <Card className="edit-card p-6 rounded-sm shadow-none">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-[#0A1128] mb-1">Gestión de usuarios</h3>
          <p className="text-xs text-[#8A8F98]">El registro público está deshabilitado. Tú decides quién entra.</p>
        </div>
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="user-create-button" className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Nuevo usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Crear usuario</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div><Label>Nombre *</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Email *</Label><Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Contraseña * (mínimo 6)</Label><Input data-testid="user-form-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm font-mono" /></div>
              <div><Label>Empresa (opcional)</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User — acceso básico</SelectItem>
                    <SelectItem value="admin">Admin — puede ver todo + gestionar contenido</SelectItem>
                    <SelectItem value="super_admin">Super admin — todo, incluido gestión de usuarios</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button data-testid="user-form-submit" onClick={submitCreate} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border-t border-[#E2DCD0] -mx-6">
        {users.map((u, idx) => {
          const meta = ROLE_LABEL[u.role] || ROLE_LABEL.user;
          const RoleIcon = meta.Icon;
          const isMe = u.id === currentUserId;
          return (
            <div
              key={u.id}
              className={`px-6 py-4 flex items-center gap-4 ${idx > 0 ? "border-t border-[#F0EBE2]" : ""}`}
              data-testid={`user-row-${u.id}`}
            >
              <div className="w-9 h-9 rounded-sm bg-[#0A1128] text-white flex items-center justify-center text-xs font-medium shrink-0">
                {(u.name || u.email).slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-[#0A1128]">{u.name}</span>
                  {isMe && <span className="text-[10px] uppercase tracking-wider text-[#D95D39]">tú</span>}
                </div>
                <div className="text-xs text-[#5C616A] truncate">{u.email}</div>
              </div>

              <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)} disabled={isMe}>
                <SelectTrigger className="w-[160px] h-9 rounded-sm" data-testid={`user-role-${u.id}`}>
                  <Badge variant="outline" className={`rounded-sm text-[10px] inline-flex items-center gap-1 ${meta.color}`}>
                    <RoleIcon className="w-3 h-3" /> {meta.label}
                  </Badge>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super admin</SelectItem>
                </SelectContent>
              </Select>

              <Button
                onClick={() => { setResetting(u); setNewPassword(""); }}
                size="icon" variant="outline"
                className="h-9 w-9 rounded-sm border-[#E2DCD0]"
                data-testid={`user-reset-${u.id}`}
                title="Resetear contraseña"
              >
                <KeyRound className="w-3.5 h-3.5" />
              </Button>
              <Button
                onClick={() => removeUser(u.id, u.name)}
                size="icon" variant="outline"
                disabled={isMe}
                className="h-9 w-9 rounded-sm border-[#E2DCD0] text-[#8A8F98] hover:text-[#c54e2e] hover:border-[#c54e2e] disabled:opacity-30"
                data-testid={`user-delete-${u.id}`}
                title="Borrar usuario"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        })}
        {users.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-[#8A8F98] italic">No hay usuarios.</div>
        )}
      </div>

      {/* Reset password modal */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent className="bg-white rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nueva contraseña para {resetting?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              type="text"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              className="rounded-sm font-mono"
              data-testid="user-reset-input"
              onKeyDown={(e) => e.key === "Enter" && resetPassword()}
            />
            <p className="text-xs text-[#8A8F98]">Apunta la contraseña antes de aceptar — no podrás volver a verla.</p>
          </div>
          <DialogFooter className="pt-3">
            <Button onClick={resetPassword} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm" data-testid="user-reset-confirm">
              Actualizar contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
